import { useSelector } from "react-redux";
import { useEffect } from "react";

import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import HomePage from "./pages/home";
import Pipeline from "./pages/pipeline";
import NewLoan from "./pages/new-loan";
import QuickPricer from "./pages/quick-pricer";
import PublicQuickPricer from "./pages/public-quick-pricer";
import UploadHistory from "./pages/upload-history";

import LoanSummary from "./pages/loan-summary";
import CreateLoan from "./pages/create-loan";
import AdminScreen from "./pages/admin-screen";
import ViewLoan from "./pages/view-loan";
import EditLoan from "./pages/edit-loan";
import ResourceLibrary from "./pages/resource-library";
import Messages from "./pages/messages";
import Settings from "./pages/settings";

import Login from "./pages/login";
import ResetPassword from "./pages/reset-password";
import SetPassword from "./pages/set-password";

import RequireAuth from "./features/auth/RequireAuth";
import { useOrganizationId } from "./features/auth/useOrganizationId";

import {
	selectCurrentToken,
	selectRefreshToken,
} from "./features/auth/authSlice";

import { storageLoad } from "./utils/localStorage";

import { initRum, getRumDiagnostics, emitRumTestEvent } from "./utils/rum";
import {
	peekExplicitLogout,
	clearExplicitLogout,
	HOME_FROM_LOCATION,
} from "./utils/logout-flag";

function App() {
	const token = useSelector(selectCurrentToken);
	const refreshToken = useSelector(selectRefreshToken);
	const localAccessToken = storageLoad("access-token");
	const localRefreshToken = storageLoad("refresh-token");
	const location = useLocation();

	// Read (without mutating) so this stays consistent even if this render
	// happens twice (React.StrictMode); cleared afterwards in the effect below.
	const skipFromOnLogout = peekExplicitLogout();

	// Fetch organization ID when organizationName is available
	useOrganizationId();

	console.log("44 process:", process.env);

	useEffect(() => {
		if (skipFromOnLogout) {
			clearExplicitLogout();
		}
	}, [skipFromOnLogout]);

	useEffect(() => {
		initRum();

		if (typeof window !== "undefined") {
			window.__rumDiagnostics = getRumDiagnostics;
			window.__rumTestEvent = emitRumTestEvent;
			window.__appBuildInfo = {
				rumDebugStamp: "rum-debug-2026-04-20-v2",
				host: window.location.host,
			};
		}
	}, []);

	return (
		<Routes>
			{/* Public, unauthenticated Quick Pricer -- reachable regardless
			    of auth state, not wrapped in RequireAuth */}
			<Route path="/public/quick-pricer" element={<PublicQuickPricer />} />

			{(token !== null && token !== undefined) ||
			(refreshToken !== null && refreshToken !== undefined) ||
			(localAccessToken !== null && localAccessToken !== undefined) ||
			(localRefreshToken !== null && localRefreshToken !== undefined) ? (
				<Route>
					{/* protected routes */}
					<Route element={<RequireAuth />}>
						<Route path="/home" element={<HomePage />} />
						<Route path="/pipeline" element={<Pipeline />} />
						<Route path="/resource-library" element={<ResourceLibrary />} />
						<Route path="/admin" element={<AdminScreen />} />
						<Route path="/messages" element={<Messages />} />
						<Route path="/settings" element={<Settings />} />
						<Route path="/quick-pricer" element={<QuickPricer />} />
						

						{/* loan submission flows */}
						<Route path="/new-loan" element={<NewLoan />} />
						<Route path="/upload-history" element={<UploadHistory />} />
						<Route path="/create-loan" element={<CreateLoan />} />
						<Route path="/edit-loan/:id" element={<EditLoan />} />
						<Route path="/view-loan/:id/:loanId" element={<ViewLoan />} />

						{/* completed loan details */}
						<Route path="/loan-summary/:id" element={<LoanSummary />} />

						<Route
							path="*"
							element={
								<Navigate
									to={
										location.state?.from
											? `${location.state.from.pathname}${
													location.state.from.search || ""
											  }`
											: "/home"
									}
									replace
								/>
							}
						/>
					</Route>
				</Route>
			) : (
				<Route>
					{/* public routes */}
					<Route path="/login" element={<Login />} />
					<Route path="/reset-password" element={<ResetPassword />} />
					<Route path="/set-password/:id" element={<SetPassword />} />
					<Route
						path="*"
						element={
							<Navigate
								to="/login"
								state={{
									from: skipFromOnLogout ? HOME_FROM_LOCATION : location,
								}}
								replace
							/>
						}
					/>
				</Route>
			)}
		</Routes>
	);
}

export default App;
